import React, { useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useDashboard } from '@/hooks/useDashboard';
import { RankingsDistributionChart } from '@/components/dashboard/RankingsDistributionChart';
import { MetricsCorrelationChart } from '@/components/dashboard/MetricsCorrelationChart';
import { MethodologyBreakdownChart } from '@/components/dashboard/MethodologyBreakdownChart';
import { TopPerformersChart } from '@/components/dashboard/TopPerformersChart';
import { Label } from "@/components/ui/label";
import { useDebounce } from '@/hooks/useDebounce';

interface FilterRanges {
  rank: [number, number];
  score: [number, number];
  gradRate: [number, number];
  socialMobility: [number, number];
}

export function DashboardPage() {
  // Local state for immediate UI updates
  const [localFilters, setLocalFilters] = useState({
    search: '',
    ranges: {
      rank: [1, 436],
      score: [0, 100],
      gradRate: [0, 100],
      socialMobility: [1, 436]
    } as FilterRanges
  });

  // Debounced filters for API calls with increased delay
  const debouncedFilters = useDebounce(localFilters, 800);

  // Use debounced filters for the API call
  const { universities, isLoading, error } = useDashboard(debouncedFilters);

  // Handler for range updates
  const handleRangeChange = useCallback((key: keyof FilterRanges, value: [number, number]) => {
    setLocalFilters(prev => ({
      ...prev,
      ranges: {
        ...prev.ranges,
        [key]: value
      }
    }));
  }, []);

  // Handler for search input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFilters(prev => ({
      ...prev,
      search: e.target.value
    }));
  }, []);

  // Handler for reset
  const handleReset = useCallback(() => {
    setLocalFilters({
      search: '',
      ranges: {
        rank: [1, 436],
        score: [0, 100],
        gradRate: [0, 100],
        socialMobility: [1, 436]
      }
    });
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500">Loading dashboard data...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-32">
              <p className="text-red-500">Error loading dashboard data</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto py-8 px-4">
      <div className="container mx-auto">
        {/* Dashboard Header */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-[#0057B7]">
              US News Ranking Dashboard
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Explore the US News dataset of 400+ National Universities. Use the filters to analyze specific ranges
              and metrics. The methodology breakdown shows how different factors contribute to the final rankings.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Filters and Visualizations Grid */}
        <div className="grid grid-cols-12 gap-8">
          {/* Filters Panel */}
          <Card className="col-span-12 md:col-span-3">
            <CardHeader>
              <CardTitle className="text-lg">Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Search */}
              <div className="space-y-2">
                <Label htmlFor="search">Search Universities</Label>
                <Input
                  id="search"
                  placeholder="Search by name..."
                  value={localFilters.search}
                  onChange={handleSearchChange}
                  className="w-full"
                />
              </div>

              {/* Rank Range */}
              <div className="space-y-2">
                <Label>National Rank Range</Label>
                <div className="pt-2">
                  <Slider
                    min={1}
                    max={436}
                    step={1}
                    value={localFilters.ranges.rank}
                    onValueChange={(value) => handleRangeChange('rank', value as [number, number])}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-[#274791] mt-1">
                    <span>#{localFilters.ranges.rank[0]}</span>
                    <span>#{localFilters.ranges.rank[1]}</span>
                  </div>
                </div>
              </div>

              {/* Score Range */}
              <div className="space-y-2">
                <Label>Overall Score Range</Label>
                <div className="pt-2">
                  <Slider
                    min={0}
                    max={100}
                    step={1}
                    value={localFilters.ranges.score}
                    onValueChange={(value) => handleRangeChange('score', value as [number, number])}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-[#274791] mt-1">
                    <span>{localFilters.ranges.score[0]}</span>
                    <span>{localFilters.ranges.score[1]}</span>
                  </div>
                </div>
              </div>

              {/* Graduation Rate Range */}
              <div className="space-y-2">
                <Label>Graduation Rate Range (%)</Label>
                <div className="pt-2">
                  <Slider
                    min={0}
                    max={100}
                    step={1}
                    value={localFilters.ranges.gradRate}
                    onValueChange={(value) => handleRangeChange('gradRate', value as [number, number])}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-[#274791] mt-1">
                    <span>{localFilters.ranges.gradRate[0]}%</span>
                    <span>{localFilters.ranges.gradRate[1]}%</span>
                  </div>
                </div>
              </div>

              {/* Social Mobility Range */}
              <div className="space-y-2">
                <Label>Social Mobility Rank Range</Label>
                <div className="pt-2">
                  <Slider
                    min={1}
                    max={436}
                    step={1}
                    value={localFilters.ranges.socialMobility}
                    onValueChange={(value) => handleRangeChange('socialMobility', value as [number, number])}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-[#274791] mt-1">
                    <span>#{localFilters.ranges.socialMobility[0]}</span>
                    <span>#{localFilters.ranges.socialMobility[1]}</span>
                  </div>
                </div>
              </div>

              {/* Reset Button */}
              <Button 
                variant="outline" 
                className="w-full mt-4 border-[#274791] text-[#274791] hover:bg-[#274791]/5"
                onClick={handleReset}
              >
                Reset Filters
              </Button>
            </CardContent>
          </Card>

          {/* Visualizations */}
          <div className="col-span-12 md:col-span-9 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>US News Ranking Methodology</CardTitle>
                  <CardDescription>
                    Breakdown of how different factors contribute to the overall university ranking
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <MethodologyBreakdownChart />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Rankings Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <RankingsDistributionChart universities={universities || []} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Metrics Correlation</CardTitle>
                </CardHeader>
                <CardContent>
                  <MetricsCorrelationChart universities={universities || []} />
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Top Performers by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <TopPerformersChart universities={universities || []} />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 